import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid
} from '@mui/material';
import { Icon } from '@iconify/react';
import { Call, PCRData } from '../types/index.ts';
import { useToast } from '../contexts/ToastContext.tsx';
import { http } from '../lib/http';

// Shared style objects (hoisted to module scope to avoid object churn)
export const FIELD_LABEL_SX = {
  color: '#b4b9c0',
  fontSize: '13px',
  fontWeight: 600,
  marginBottom: '6px',
  display: 'block'
};

export const TEXTFIELD_SX = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#16181d',
    '& fieldset': { borderColor: '#464a52' },
    '&:hover fieldset': { borderColor: '#3c8cff' },
    '&.Mui-focused fieldset': { borderColor: '#3c8cff' }
  },
  '& .MuiInputBase-input': { 
    color: '#eceef1', 
    fontSize: '14px',
    padding: '12px 14px'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderWidth: '1px'
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderWidth: '1px'
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: '2px'
  }
};

// Section header component (moved to module scope for proper memoization)
export const SectionHeader = React.memo(
  ({ icon, title }: { icon: string; title: string }) => (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1.5,
      mb: 2,
      mt: 3,
      pb: 1,
      borderBottom: '2px solid #2a2d32'
    }}>
      <Icon icon={icon} width="24" height="24" style={{ color: '#3c8cff' }} />
      <Typography variant="h6" sx={{ 
        color: '#eceef1', 
        fontWeight: 600,
        fontSize: '16px',
        letterSpacing: '0.5px'
      }}>
        {title}
      </Typography>
    </Box>
  )
);

// Memoized field component for performance optimization
const MemoField = React.memo(function MemoField(props: {
  label: string; 
  value: string; 
  onChange: (v: string) => void; 
  tfProps: any; 
  rows?: number; 
  multiline?: boolean; 
  placeholder?: string;
  ariaLabel?: string;
  spellCheck?: boolean;
  autoComplete?: string;
}) {
  const { label, value, onChange, tfProps, ariaLabel, spellCheck = false, autoComplete = 'off', ...rest } = props;

  return (
    <>
      <Typography sx={FIELD_LABEL_SX}>{label}</Typography>
      <TextField
        fullWidth
        value={value}
        onChange={(e) => onChange(e.target.value)}
        variant="outlined"
        size="small"
        sx={TEXTFIELD_SX}
        aria-label={ariaLabel}
        inputRef={tfProps.inputRef}
        inputProps={{ 
          ...tfProps.inputProps, 
          spellCheck, 
          autoComplete 
        }}
        {...rest}
      />
    </>
  );
});

interface PCRModalProps {
  open: boolean;
  onClose: () => void;
  call: Call;
  currentUsername?: string;
  editPCRData?: PCRData;
  editCommentId?: string;
  onPCRSaved?: () => void;
}

const PCRModal: React.FC<PCRModalProps> = ({
  open,
  onClose,
  call,
  currentUsername,
  editPCRData,
  editCommentId,
  onPCRSaved
}) => {
  const { showSuccess, showError } = useToast();
  const [pcrData, setPCRData] = useState<PCRData>({
    name: '',
    age: '',
    weight: '',
    pastMedicalHistory: '',
    chiefComplaint: '',
    chiefComplaintExplanation: '',
    physicalAssessment: '',
    recentVitals: '',
    interventionsPerformed: '',
    medicationsAdministered: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [autoDraftCommentId, setAutoDraftCommentId] = useState<string | null>(null);
  const [autosaveCountdown, setAutosaveCountdown] = useState<number | null>(null);
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);
  const [discordThreadId, setDiscordThreadId] = useState<string>('');
  
  // Refs for cursor position tracking
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>({});
  const initialPCRDataRef = useRef<PCRData | null>(null);
  const cursorPositionsRef = useRef<{ [key: string]: number }>({});
  const pendingCursorRestoration = useRef<{ [key: string]: number }>({});
  
  // Refs for stable autosave
  const pcrRef = useRef(pcrData);
  const unsavedRef = useRef(hasUnsavedChanges);
  const submittingRef = useRef(isSubmitting);
  const composingRef = useRef(false);

  useEffect(() => {
    if (editPCRData) {
      setPCRData(editPCRData);
      initialPCRDataRef.current = editPCRData;
    } else {
      const emptyData = {
        name: '',
        age: '',
        weight: '',
        pastMedicalHistory: '',
        chiefComplaint: '',
        chiefComplaintExplanation: '',
        physicalAssessment: '',
        recentVitals: '',
        interventionsPerformed: '',
        medicationsAdministered: '',
        notes: ''
      };
      setPCRData(emptyData);
      initialPCRDataRef.current = emptyData;
    }
    setHasUnsavedChanges(false);
    setAutoDraftCommentId(null);
    setAutosaveCountdown(null);
    setLastSavedTime(null);
    setDiscordThreadId('');
  }, [open, call.id, editPCRData]);

  // Keep refs in sync with state for stable autosave
  useEffect(() => { pcrRef.current = pcrData; }, [pcrData]);
  useEffect(() => { unsavedRef.current = hasUnsavedChanges; }, [hasUnsavedChanges]);
  useEffect(() => { submittingRef.current = isSubmitting; }, [isSubmitting]);
  
  // Track autoDraftCommentId in ref to prevent interval churn
  const autoDraftIdRef = useRef<string | null>(null);
  useEffect(() => { autoDraftIdRef.current = autoDraftCommentId; }, [autoDraftCommentId]);

  // Check if PCR data has meaningful content (not just empty strings)
  const hasContent = useCallback((data: PCRData) => {
    return Object.values(data).some(value => value.trim().length > 0);
  }, []);

  // Fields that should be uppercase
  const uppercaseFields = new Set<keyof PCRData>([
    'name', 'pastMedicalHistory', 'chiefComplaint', 'chiefComplaintExplanation',
    'physicalAssessment', 'recentVitals', 'interventionsPerformed', 
    'medicationsAdministered', 'notes'
  ]);

  // Countdown timer for autosave
  useEffect(() => {
    if (!open || !hasUnsavedChanges || isSubmitting || !hasContent(pcrData)) {
      setAutosaveCountdown(null);
      return;
    }

    // Start countdown from 15 seconds when unsaved changes appear
    setAutosaveCountdown(15);
    
    const countdownInterval = setInterval(() => {
      setAutosaveCountdown(prev => {
        if (prev === null || prev <= 1) {
          return null;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [open, hasUnsavedChanges, isSubmitting, hasContent]);

  // Stable auto-draft interval using refs
  useEffect(() => {
    if (!open) return;
    
    const intervalId = setInterval(async () => {
      // Read latest values from refs to avoid dependency churn
      if (!unsavedRef.current || submittingRef.current || !hasContent(pcrRef.current)) {
        return;
      }

    try {
      const author = currentUsername || 'UNKNOWN';
        const commentIdToEdit = autoDraftIdRef.current || editCommentId;
        
      const response = await http.post(`/calls/${call.id}/pcr`, {
          pcrData: pcrRef.current,
        author,
        action: 'draft',
          editCommentId: commentIdToEdit
        });
        
        // Store the comment ID from the first auto-draft for future updates
        if (response?.data?.data?.id && !autoDraftIdRef.current) {
          setAutoDraftCommentId(response.data.data.id);
        }
        
        setHasUnsavedChanges(false);
        setLastSavedTime(new Date());
        setAutosaveCountdown(null);
        initialPCRDataRef.current = { ...pcrRef.current };
        
        showSuccess('PCR auto-saved as draft');
        console.debug('[PCRModal] Auto-draft saved successfully');
      } catch (error) {
        console.error('[PCRModal] Auto-draft failed:', error);
        // Don't show error to user for auto-draft failures
      }
    }, 15000);
    
    return () => clearInterval(intervalId);
  }, [open, call.id, currentUsername, editCommentId, hasContent, showSuccess]);

  // Handle tab visibility changes and page unload for final saves
  useEffect(() => {
    if (!open) return;

    const onVisibility = () => {
      // Trigger one last save when hiding the tab
      if (document.visibilityState === 'hidden' && unsavedRef.current && hasContent(pcrRef.current)) {
        const author = currentUsername || 'UNKNOWN';
        const commentIdToEdit = autoDraftIdRef.current || editCommentId;
        http.post(`/calls/${call.id}/pcr`, { 
          pcrData: pcrRef.current, 
          author, 
          action: 'draft', 
          editCommentId: commentIdToEdit 
        }).catch(() => {});
      }
    };

    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (unsavedRef.current && hasContent(pcrRef.current)) {
        // Best-effort fire-and-forget using sendBeacon
        if (navigator.sendBeacon) {
          navigator.sendBeacon(
            `/calls/${call.id}/pcr`,
            new Blob([JSON.stringify({ 
              pcrData: pcrRef.current, 
              author: currentUsername || 'UNKNOWN', 
              action: 'draft', 
              editCommentId: autoDraftIdRef.current || editCommentId,
              beacon: true // Flag for server to relax CSRF/return 204
            })], { type: 'application/json' })
          );
        }
        // Show native prompt for unsaved changes
        e.preventDefault();
        e.returnValue = '';
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('beforeunload', onBeforeUnload);
    
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [open, call.id, currentUsername, editCommentId, hasContent]);


  // Store cursor positions before any state updates
  const storeCursorPosition = useCallback((field: keyof PCRData) => {
    const input = inputRefs.current[field];
    if (input && document.activeElement === input) {
      const position = input.selectionStart || 0;
      cursorPositionsRef.current[field] = position;
    }
  }, []);

  // Handle cursor position tracking events
  const handleCursorPositionChange = useCallback((field: keyof PCRData) => {
    return () => {
      const input = inputRefs.current[field];
      if (input && document.activeElement === input) {
        const position = input.selectionStart || 0;
        cursorPositionsRef.current[field] = position;
      }
    };
  }, []);

  // Helper function to get common TextField props with cursor tracking
  const getTextFieldProps = useCallback((field: keyof PCRData, options: { numeric?: boolean; allowDecimals?: boolean } = {}) => ({
    inputRef: (el: HTMLInputElement | HTMLTextAreaElement | null) => { 
      inputRefs.current[field] = el; 
    },
    inputProps: {
      onCompositionStart: () => { composingRef.current = true; },
      onCompositionEnd: () => { composingRef.current = false; },
      onFocus: handleCursorPositionChange(field),
      onSelect: handleCursorPositionChange(field),
      onClick: handleCursorPositionChange(field),
      onKeyUp: handleCursorPositionChange(field),
      ...(options.numeric ? { 
        inputMode: 'numeric' as const, 
        pattern: options.allowDecimals ? '[0-9]*\\.?[0-9]*' : '[0-9]*'
      } : {})
    }
  }), [handleCursorPositionChange]);

  // Restore cursor positions after DOM updates (optimized)
  useLayoutEffect(() => {
    const keys = Object.keys(pendingCursorRestoration.current);
    if (!keys.length) return; // Skip RAF if no restorations needed
    
    keys.forEach(field => {
      const position = pendingCursorRestoration.current[field];
      const input = inputRefs.current[field];
      
      if (input && document.activeElement === input && position !== undefined) {
        requestAnimationFrame(() => {
          // Extra guard: check if element is still active and available
          const el = inputRefs.current[field];
          if (!el || document.activeElement !== el) return;
          
          try {
            // Clamp position to input value length to handle cases where value is shorter
            const clampedPosition = Math.min(position, el.value.length);
            el.setSelectionRange(clampedPosition, clampedPosition);
          } catch (error) {
            // Silently handle cursor restoration failures
          }
        });
      }
    });
    
    // Clear pending restorations
    pendingCursorRestoration.current = {};
  });

  const handleFieldChange = (field: keyof PCRData, rawValue: string) => {
    // Store cursor position before state update
    storeCursorPosition(field);
    
    // Mark this field for cursor restoration
    const currentPosition = cursorPositionsRef.current[field];
    if (currentPosition !== undefined) {
      pendingCursorRestoration.current[field] = currentPosition;
    }
    
    // Numeric field validation - hard filter invalid characters
    if (field === 'age' && rawValue) {
      rawValue = rawValue.replace(/[^\d]/g, ''); // Age: integers only
    }
    if (field === 'weight' && rawValue) {
      // Weight: allow decimals, prevent multiple decimal points
      rawValue = rawValue.replace(/[^\d.]/g, '').replace(/(\..*?)\..*/g, '$1');
    }
    
    setPCRData(prev => {
      // Handle uppercase transformation with IME composition safety
      const nextValue = uppercaseFields.has(field) 
        ? (composingRef.current ? rawValue : rawValue.toUpperCase())
        : rawValue;
      
      // Simple sticky dirty flag - only check if this specific field changed
      if (!hasUnsavedChanges && nextValue !== prev[field]) {
        setHasUnsavedChanges(true);
      }
      
      return { ...prev, [field]: nextValue };
    });
  };


  const handleSendToHospital = async () => {
    setIsSubmitting(true);
    try {
      const author = currentUsername || 'UNKNOWN';
      
      // Use autoDraftCommentId if available (for updating auto-drafted PCR), otherwise editCommentId
      const commentIdToEdit = autoDraftCommentId || editCommentId;
      
      const response = await http.post(`/calls/${call.id}/pcr`, {
        pcrData,
        author,
        action: 'hospital',
        editCommentId: commentIdToEdit,
        threadId: discordThreadId.trim() || undefined
      });

      if (response.status === 200) {
        showSuccess('PCR sent to hospital');
        onPCRSaved?.();
    setHasUnsavedChanges(false);
    setAutosaveCountdown(null);
    setLastSavedTime(null);
    initialPCRDataRef.current = { ...pcrData };
    
    // Clear auto-draft comment ID since PCR is now sent to hospital
    setAutoDraftCommentId(null);
        
        onClose();
      } else {
        showError('Failed to send PCR to hospital');
      }
    } catch (error) {
      showError('Failed to send PCR to hospital');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    // Perform final auto-draft if there are unsaved changes
    if (hasUnsavedChanges && hasContent(pcrData)) {
      try {
        const author = currentUsername || 'UNKNOWN';
        
        // Use autoDraftCommentId if available, otherwise editCommentId
        const commentIdToEdit = autoDraftCommentId || editCommentId;
        
        await http.post(`/calls/${call.id}/pcr`, {
          pcrData,
          author,
          action: 'draft',
          editCommentId: commentIdToEdit
        });
        setLastSavedTime(new Date());
        setAutosaveCountdown(null);
        showSuccess('PCR auto-saved as draft before closing');
      } catch (error) {
        console.error('Final auto-draft failed:', error);
        // Don't prevent closing if auto-draft fails
      }
    }
    onClose();
  };


  // Stable tfProps for MemoField to prevent unnecessary re-renders
  const nameTFProps = React.useMemo(() => getTextFieldProps('name'), [getTextFieldProps]);

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        // Prevent accidental close when there are unsaved edits (only backdrop, let Esc show prompt)
        if (hasUnsavedChanges && reason === 'backdropClick') {
          return;
        }
        handleCancel();
      }}
      maxWidth="lg"
      fullWidth
      keepMounted // Avoids full unmount/remount cost
      PaperProps={{
        sx: {
          backgroundColor: '#1c1f25',
          border: '1px solid #40444b',
          borderRadius: '8px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        color: '#ffffff', 
        borderBottom: '1px solid #40444b',
        background: 'linear-gradient(135deg, #1c1f25 0%, #1a1d22 100%)',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Icon icon="mdi:file-document-outline" width="28" height="28" style={{ color: '#3c8cff' }} />
            <Typography variant="h5" component="div" sx={{ fontWeight: 700, letterSpacing: '0.5px' }}>
              {editPCRData ? 'Edit Patient Care Report' : 'Patient Care Report'}
            </Typography>
          </Box>
          {hasUnsavedChanges && hasContent(pcrData) && !isSubmitting && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
              {autosaveCountdown !== null && autosaveCountdown > 0 ? (
                <Typography variant="body2" sx={{ 
                  color: '#3c8cff', 
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}>
                  <Icon icon="mdi:clock-outline" width="16" height="16" />
                  Auto-saving in {autosaveCountdown}s
                </Typography>
              ) : lastSavedTime ? (
                <Typography variant="body2" sx={{ 
                  color: '#4caf50', 
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}>
                  <Icon icon="mdi:check-circle" width="16" height="16" />
                  Saved {lastSavedTime.toLocaleTimeString()}
                </Typography>
              ) : null}
            </Box>
          )}
        </Box>
        <Typography variant="body2" sx={{ color: '#b4b9c0', ml: 5, fontSize: '13px' }}>
          <Icon icon="mdi:map-marker" width="16" height="16" style={{ verticalAlign: 'middle', marginRight: '4px' }} />
          Incident #{call.id} • {call.location}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ 
        padding: 3, 
        backgroundColor: '#1a1d22',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px'
        },
        '&::-webkit-scrollbar-track': {
          background: '#16181d'
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#40444b',
          borderRadius: '4px'
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: '#4a4e55'
        }
      }}>
        <Box>
          {/* Patient Information Section */}
          <SectionHeader icon="mdi:account-outline" title="Patient Information" />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <MemoField
                label="Username"
                value={pcrData.name}
                onChange={(value) => handleFieldChange('name', value)}
                tfProps={nameTFProps}
                placeholder="Enter patient's username"
                ariaLabel="Patient username"
                spellCheck={false}
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography sx={FIELD_LABEL_SX}>Age</Typography>
              <TextField
                fullWidth
                placeholder="Age"
                value={pcrData.age}
                onChange={(e) => handleFieldChange('age', e.target.value)}
                variant="outlined"
                size="small"
                sx={TEXTFIELD_SX}
                aria-label="Patient age"
                helperText={!pcrData.age ? '' : Number(pcrData.age) > 120 ? 'Age looks high' : ''}
                inputProps={{ 
                  ...getTextFieldProps('age', { numeric: true }).inputProps, 
                  min: 0 
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography sx={FIELD_LABEL_SX}>Weight (lbs)</Typography>
              <TextField
                fullWidth
                placeholder="Weight"
                value={pcrData.weight}
                onChange={(e) => handleFieldChange('weight', e.target.value)}
                variant="outlined"
                size="small"
                sx={TEXTFIELD_SX}
                aria-label="Patient weight in pounds"
                helperText={!pcrData.weight ? '' : Number(pcrData.weight) > 1000 ? 'Weight looks high' : ''}
                inputProps={{ 
                  ...getTextFieldProps('weight', { numeric: true, allowDecimals: true }).inputProps, 
                  min: 0 
                }}
              />
            </Grid>
          </Grid>

          {/* Medical History Section */}
          <SectionHeader icon="mdi:clipboard-text-outline" title="Medical History" />
          <Typography sx={FIELD_LABEL_SX}>Past Medical History, Allergies, Current Medications</Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Document any known medical history, allergies, or current medications..."
            value={pcrData.pastMedicalHistory}
            onChange={(e) => handleFieldChange('pastMedicalHistory', e.target.value)}
            variant="outlined"
            size="small"
            sx={TEXTFIELD_SX}
            {...getTextFieldProps('pastMedicalHistory')}
          />

          {/* Chief Complaint Section */}
          <SectionHeader icon="mdi:alert-circle-outline" title="Chief Complaint" />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MemoField
                label="Primary Complaint"
                value={pcrData.chiefComplaint}
                onChange={(value) => handleFieldChange('chiefComplaint', value)}
                tfProps={React.useMemo(() => getTextFieldProps('chiefComplaint'), [getTextFieldProps])}
                placeholder="What is the patient's main complaint?"
                ariaLabel="Primary complaint description"
                spellCheck={false}
                autoComplete="off"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={FIELD_LABEL_SX}>Detailed Explanation</Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Provide detailed information about the chief complaint..."
                value={pcrData.chiefComplaintExplanation}
                onChange={(e) => handleFieldChange('chiefComplaintExplanation', e.target.value)}
                variant="outlined"
                size="small"
                sx={TEXTFIELD_SX}
                {...getTextFieldProps('chiefComplaintExplanation')}
              />
            </Grid>
          </Grid>

          {/* Assessment Section */}
          <SectionHeader icon="mdi:stethoscope" title="Physical Assessment & Vitals" />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography sx={FIELD_LABEL_SX}>Physical Assessment</Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="Document physical examination findings..."
                value={pcrData.physicalAssessment}
                onChange={(e) => handleFieldChange('physicalAssessment', e.target.value)}
                variant="outlined"
                size="small"
                sx={TEXTFIELD_SX}
                inputProps={{ 
                  ...getTextFieldProps('physicalAssessment').inputProps, 
                  spellCheck: false, 
                  autoComplete: 'off' 
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={FIELD_LABEL_SX}>Recent Vitals</Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="BP, HR, RR, SpO2, Temp, GCS..."
                value={pcrData.recentVitals}
                onChange={(e) => handleFieldChange('recentVitals', e.target.value)}
                variant="outlined"
                size="small"
                sx={TEXTFIELD_SX}
                {...getTextFieldProps('recentVitals')}
              />
            </Grid>
          </Grid>

          {/* Treatment Section */}
          <SectionHeader icon="mdi:medical-bag" title="Treatment & Interventions" />
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography sx={FIELD_LABEL_SX}>Interventions Performed</Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="List all procedures and interventions performed..."
                value={pcrData.interventionsPerformed}
                onChange={(e) => handleFieldChange('interventionsPerformed', e.target.value)}
                variant="outlined"
                size="small"
                sx={TEXTFIELD_SX}
                {...getTextFieldProps('interventionsPerformed')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={FIELD_LABEL_SX}>Medications Administered</Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="List all medications given, including dosage and route..."
                value={pcrData.medicationsAdministered}
                onChange={(e) => handleFieldChange('medicationsAdministered', e.target.value)}
                variant="outlined"
                size="small"
                sx={TEXTFIELD_SX}
                {...getTextFieldProps('medicationsAdministered')}
              />
            </Grid>
          </Grid>

          {/* Narrative Section */}
          <SectionHeader icon="mdi:note-text-outline" title="Narrative" />
          <Typography sx={FIELD_LABEL_SX}>Narrative</Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Include any additional run narrative as applicable to the above..."
            value={pcrData.notes}
            onChange={(e) => handleFieldChange('notes', e.target.value)}
            variant="outlined"
            size="small"
            sx={TEXTFIELD_SX}
            {...getTextFieldProps('notes')}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        padding: '12px 20px', 
        borderTop: '1px solid #40444b',
        backgroundColor: '#1c1f25',
        gap: 1.5,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: 'auto'
      }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          flex: 1,
          minWidth: 0,
          maxWidth: '350px'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            width: '100%'
          }}>
            <Icon icon="mdi:discord" width="18" height="18" style={{ color: '#5865f2', flexShrink: 0 }} />
            <TextField
              fullWidth
              placeholder="Existing SCMC Discord Thread ID (optional)"
              value={discordThreadId}
              onChange={(e) => setDiscordThreadId(e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                ...TEXTFIELD_SX,
                '& .MuiOutlinedInput-root': {
                  ...TEXTFIELD_SX['& .MuiOutlinedInput-root'],
                  fontSize: '12px',
                  padding: '6px 10px',
                  height: '32px'
                },
                '& .MuiInputBase-input': {
                  ...TEXTFIELD_SX['& .MuiInputBase-input'],
                  padding: 0,
                  fontSize: '12px'
                },
                '& .MuiFormHelperText-root': {
                  display: 'none'
                }
              }}
              inputProps={{
                spellCheck: false,
                autoComplete: 'off'
              }}
            />
          </Box>
          <Typography sx={{
            fontSize: '10px',
            marginTop: '2px',
            marginLeft: '26px',
            fontStyle: 'italic',
            color: '#8a8e95',
            lineHeight: 1.2
          }}>
            Leave empty for new thread
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button
            onClick={handleCancel}
            startIcon={<Icon icon="mdi:close" width="16" height="16" />}
            sx={{
              color: '#b4b9c0',
              fontSize: '13px',
              textTransform: 'none',
              padding: '6px 16px',
              minHeight: '32px',
              '&:hover': { 
                backgroundColor: '#2a2d32',
                color: '#eceef1'
              }
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          
          <Button
            onClick={handleSendToHospital}
            variant="contained"
            startIcon={<Icon icon="mdi:hospital-building" width="16" height="16" />}
            sx={{
              backgroundColor: '#3c8cff',
              color: '#ffffff',
              fontSize: '13px',
              fontWeight: 600,
              textTransform: 'none',
              padding: '6px 20px',
              minHeight: '32px',
              boxShadow: '0 2px 8px rgba(60, 140, 255, 0.3)',
              '&:hover': { 
                backgroundColor: '#2a7ae9',
                boxShadow: '0 4px 12px rgba(60, 140, 255, 0.4)'
              },
              '&:disabled': { 
                backgroundColor: '#464a52',
                color: '#8a8e95',
                boxShadow: 'none'
              }
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send to Hospital'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PCRModal;
